import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../../components/Layout';
import { apiFetch } from '../../lib/api';
import { useFiltroSocieta } from '../../hooks/useFiltroSocieta';
import { Dipendente, ScadenzaFlag, StatoDipendente, TipoContratto } from '../../types';
import { showToast } from '../../components/Toast';
import ApiInspector from '../../components/ApiInspector';
import { API_DOCS } from './ListaDipendenti.api';
import './ListaDipendenti.css';

const STATO_BADGE: Record<StatoDipendente, string> = {
  ATTIVO:      'bop-badge--success',
  ASPETTATIVA: 'bop-badge--warning',
  CESSATO:     'bop-badge--neutral',
};

const SCADENZA_BADGE: Record<ScadenzaFlag, string> = {
  VERDE:   'bop-badge--success',
  GIALLO:  'bop-badge--warning',
  ARANCIO: 'bop-badge--warning',
  ROSSO:   'bop-badge--danger',
};

const CONTRATTO_LABEL: Record<TipoContratto, string> = {
  INDETERMINATO: 'Indeterminato',
  DETERMINATO:   'Determinato',
  STAGE:         'Stage',
};

const EMPTY_FORM_NUOVO = {
  cognome:          '',
  nome:             '',
  codiceFiscale:    '',
  dataNascita:      '',
  dataAssunzione:   '',
  tipoContratto:    'INDETERMINATO',
  dataFineContratto: '',
  residenza:        '',
  telefono:         '',
  email:            '',
  mansione:         '',
  ccnl:             '',
  ral:              '',
};

export default function ListaDipendenti() {
  const navigate = useNavigate();
  const [dipendenti, setDipendenti] = useState<Dipendente[]>([]);
  const [loading,    setLoading]    = useState(true);
  const [error,      setError]      = useState('');

  const {
    societaId: fSocieta,
    setSocietaId: setFSocieta,
    listaSocieta: societa,
    isUnica,
    pronta,
  } = useFiltroSocieta();

  const [fStato,     setFStato]     = useState('');
  const [fContratto, setFContratto] = useState('');
  const [fSearch,    setFSearch]    = useState('');

  /* ── Modale Nuovo Dipendente ── */
  const [showModalNuovo, setShowModalNuovo] = useState(false);
  const [formNuovo,      setFormNuovo]      = useState({ ...EMPTY_FORM_NUOVO });
  const [savingNuovo,    setSavingNuovo]    = useState(false);
  const [errorNuovo,     setErrorNuovo]     = useState('');

  const fn = (k: string) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
      setFormNuovo((p) => ({ ...p, [k]: e.target.value }));

  const openModalNuovo = () => {
    setFormNuovo({ ...EMPTY_FORM_NUOVO });
    setErrorNuovo('');
    setShowModalNuovo(true);
  };

  const handleSalvaNuovo = async () => {
    setSavingNuovo(true);
    setErrorNuovo('');
    try {
      const body = {
        societaId:         fSocieta ? parseInt(fSocieta) : undefined,
        nome:              formNuovo.nome.trim(),
        cognome:           formNuovo.cognome.trim(),
        codiceFiscale:     formNuovo.codiceFiscale.trim(),
        dataNascita:       formNuovo.dataNascita || null,
        residenza:         formNuovo.residenza.trim() || null,
        telefono:          formNuovo.telefono.trim() || null,
        email:             formNuovo.email.trim() || null,
        dataAssunzione:    formNuovo.dataAssunzione || null,
        tipoContratto:     formNuovo.tipoContratto,
        dataFineContratto: formNuovo.tipoContratto !== 'INDETERMINATO' ? (formNuovo.dataFineContratto || null) : null,
        mansione:          formNuovo.mansione.trim() || null,
        ccnl:              formNuovo.ccnl.trim() || null,
        ral:               formNuovo.ral ? parseFloat(formNuovo.ral) : null,
      };
      // @api POST /api/dipendenti {societaId,nome,cognome,codiceFiscale,dataNascita,residenza,telefono,email,dataAssunzione,tipoContratto,dataFineContratto,mansione,ccnl,ral} -> {dipendente:{id,...}}
      await apiFetch<{ dipendente: Dipendente }>('/api/dipendenti', {
        method: 'POST',
        body: JSON.stringify(body),
      });
      setShowModalNuovo(false);
      showToast('Dipendente creato con successo', 'success');
      setLoading(true);
      load().catch((e) => setError(e.message)).finally(() => setLoading(false));
    } catch (e: any) {
      setErrorNuovo(e.message || 'Errore durante il salvataggio');
    } finally {
      setSavingNuovo(false);
    }
  };

  /* ── Caricamento lista ── */
  const load = async () => {
    if (!pronta) return;
    const params = new URLSearchParams();
    if (fSocieta)   params.set('societaId',    fSocieta);
    if (fStato)     params.set('stato',         fStato);
    if (fContratto) params.set('tipoContratto', fContratto);
    if (fSearch)    params.set('search',        fSearch);
    // @api GET /api/dipendenti?societaId&stato&tipoContratto&search -> {dipendenti:[{id,nome,cognome,codiceFiscale,mansione,tipoContratto,dataFineContratto,scadenzaFlag,stato}]}
    const data = await apiFetch<{ dipendenti: Dipendente[] }>(`/api/dipendenti?${params}`);
    setDipendenti(data.dipendenti);
  };

  useEffect(() => {
    setLoading(true);
    load().catch((e) => setError(e.message)).finally(() => setLoading(false));
  }, [pronta, fSocieta, fStato, fContratto, fSearch]); // eslint-disable-line

  const fmtDate = (d?: string | null) => d ? new Date(d).toLocaleDateString('it-IT') : '—';

  // Bottone abilitato solo quando c'è una società concreta:
  // - isUnica: topbar o utente mono-società (fSocieta già valorizzata)
  // - !isUnica && fSocieta: superadmin/multi ha scelto una società dal filtro
  // pronta è sempre true per superadmin → serve il check su fSocieta
  const abilitaNuovo = pronta && (isUnica || !!fSocieta);

  return (
    <Layout title="Dipendenti">
      {/* Header */}
      <div className="bop-page-header">
        <h1 className="bop-page-title">Dipendenti</h1>
        <button
          className="bop-btn bop-btn-primary"
          disabled={!abilitaNuovo}
          onClick={openModalNuovo}
        >
          + Nuovo Dipendente
        </button>
      </div>

      {/* Filtri */}
      <div className="bop-filter-bar">
        {!isUnica && (
          <select
            className="bop-select bop-lista-dip__select-societa"
            value={fSocieta}
            onChange={(e) => setFSocieta(e.target.value)}
          >
            <option value="">Tutte le società</option>
            {societa.map((s) => <option key={s.id} value={s.id}>{s.nome}</option>)}
          </select>
        )}
        <select
          className="bop-select bop-lista-dip__select-stato"
          value={fStato}
          onChange={(e) => setFStato(e.target.value)}
        >
          <option value="">Tutti gli stati</option>
          <option value="ATTIVO">Attivo</option>
          <option value="ASPETTATIVA">Aspettativa</option>
          <option value="CESSATO">Cessato</option>
        </select>
        <select
          className="bop-select bop-lista-dip__select-contratto"
          value={fContratto}
          onChange={(e) => setFContratto(e.target.value)}
        >
          <option value="">Tutti i contratti</option>
          <option value="INDETERMINATO">Indeterminato</option>
          <option value="DETERMINATO">Determinato</option>
          <option value="STAGE">Stage</option>
        </select>
        <input
          className="bop-input bop-lista-dip__search"
          placeholder="Cerca nome / CF…"
          value={fSearch}
          onChange={(e) => setFSearch(e.target.value)}
        />
      </div>

      {error && <div className="inline-error">{error}</div>}

      <div className="bop-card">
        <div className="bop-table-wrapper">
          {loading ? (
            <div className="inline-loading">Caricamento…</div>
          ) : dipendenti.length === 0 ? (
            <div className="bop-empty-state">Nessun dipendente trovato.</div>
          ) : (
            <table className="bop-table bop-table--clickable">
              <thead>
                <tr>
                  <th>Cognome e Nome</th>
                  <th>Cod. Fiscale</th>
                  {!isUnica && <th>Società</th>}
                  <th>Mansione</th>
                  <th>Contratto</th>
                  <th>Scadenza</th>
                  <th className="bop-text-center bop-lista-dip__th-stato">Stato</th>
                </tr>
              </thead>
              <tbody>
                {dipendenti.map((d) => (
                  <tr
                    key={d.id}
                    onClick={() => navigate(`/aziendale/dipendenti/${d.id}`)}
                  >
                    <td><strong>{d.cognome} {d.nome}</strong></td>
                    <td className="bop-lista-dip__cf-cell">{d.codiceFiscale}</td>
                    {!isUnica && <td className="bop-text-muted">{d.societaNome || '—'}</td>}
                    <td className="bop-text-muted">{d.mansione || '—'}</td>
                    <td>{CONTRATTO_LABEL[d.tipoContratto]}</td>
                    <td>
                      {d.dataFineContratto ? (
                        <span>
                          {fmtDate(d.dataFineContratto)}
                          {d.scadenzaFlag && (
                            <span className={`bop-badge ${SCADENZA_BADGE[d.scadenzaFlag]} bop-lista-dip__scadenza-badge`}>
                              {d.scadenzaFlag}
                            </span>
                          )}
                        </span>
                      ) : '—'}
                    </td>
                    <td className="bop-text-center">
                      <span className={`bop-badge ${STATO_BADGE[d.stato]}`}>{d.stato}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* ── Modale Nuovo Dipendente ── */}
      {showModalNuovo && (
        <div className="bop-modal-overlay">
          <div className="bop-modal bop-lista-dip__modal-nuovo">
            <div className="bop-modal__header">
              <h3 className="bop-modal__title">Nuovo Dipendente</h3>
              <button
                className="bop-btn bop-btn-ghost bop-modal__close"
                onClick={() => setShowModalNuovo(false)}
                disabled={savingNuovo}
              >
                ✕
              </button>
            </div>
            <div className="bop-modal__body">
              {errorNuovo && (
                <div className="bop-lista-dip__modal-error">{errorNuovo}</div>
              )}

              <div className="bop-form-row">
                <div className="bop-form-group">
                  <label className="bop-label">Cognome *</label>
                  <input
                    className="bop-input"
                    value={formNuovo.cognome}
                    onChange={fn('cognome')}
                    placeholder="Cognome"
                  />
                </div>
                <div className="bop-form-group">
                  <label className="bop-label">Nome *</label>
                  <input
                    className="bop-input"
                    value={formNuovo.nome}
                    onChange={fn('nome')}
                    placeholder="Nome"
                  />
                </div>
              </div>

              <div className="bop-form-row">
                <div className="bop-form-group">
                  <label className="bop-label">Codice Fiscale *</label>
                  <input
                    className="bop-input"
                    value={formNuovo.codiceFiscale}
                    onChange={fn('codiceFiscale')}
                    placeholder="RSSMRA80A01H501Z"
                    maxLength={16}
                  />
                </div>
                <div className="bop-form-group">
                  <label className="bop-label">Data di Nascita *</label>
                  <input
                    className="bop-input bop-lista-dip__date-input"
                    type="date"
                    value={formNuovo.dataNascita}
                    onChange={fn('dataNascita')}
                  />
                </div>
              </div>

              <div className="bop-form-row">
                <div className="bop-form-group">
                  <label className="bop-label">Data Assunzione *</label>
                  <input
                    className="bop-input bop-lista-dip__date-input"
                    type="date"
                    value={formNuovo.dataAssunzione}
                    onChange={fn('dataAssunzione')}
                  />
                </div>
                <div className="bop-form-group">
                  <label className="bop-label">Tipo Contratto *</label>
                  <select
                    className="bop-select"
                    value={formNuovo.tipoContratto}
                    onChange={fn('tipoContratto')}
                  >
                    <option value="INDETERMINATO">Indeterminato</option>
                    <option value="DETERMINATO">Determinato</option>
                    <option value="STAGE">Stage</option>
                  </select>
                </div>
              </div>

              {formNuovo.tipoContratto !== 'INDETERMINATO' && (
                <div className="bop-form-group">
                  <label className="bop-label">Data Fine Contratto</label>
                  <input
                    className="bop-input bop-lista-dip__date-input"
                    type="date"
                    value={formNuovo.dataFineContratto}
                    onChange={fn('dataFineContratto')}
                  />
                </div>
              )}

              <div className="bop-form-group">
                <label className="bop-label">Residenza</label>
                <input
                  className="bop-input"
                  value={formNuovo.residenza}
                  onChange={fn('residenza')}
                  placeholder="Via, Città"
                />
              </div>

              <div className="bop-form-row">
                <div className="bop-form-group">
                  <label className="bop-label">Telefono</label>
                  <input
                    className="bop-input"
                    type="tel"
                    value={formNuovo.telefono}
                    onChange={fn('telefono')}
                    placeholder="+39 333 1234567"
                  />
                </div>
                <div className="bop-form-group">
                  <label className="bop-label">Email</label>
                  <input
                    className="bop-input"
                    type="email"
                    value={formNuovo.email}
                    onChange={fn('email')}
                    placeholder="mario.rossi@esempio.it"
                  />
                </div>
              </div>

              <div className="bop-form-row">
                <div className="bop-form-group">
                  <label className="bop-label">Mansione</label>
                  <input
                    className="bop-input"
                    value={formNuovo.mansione}
                    onChange={fn('mansione')}
                    placeholder="es. Responsabile Vendite"
                  />
                </div>
                <div className="bop-form-group">
                  <label className="bop-label">CCNL</label>
                  <input
                    className="bop-input"
                    value={formNuovo.ccnl}
                    onChange={fn('ccnl')}
                    placeholder="es. Commercio"
                  />
                </div>
              </div>

              <div className="bop-form-group">
                <label className="bop-label">RAL (€)</label>
                <input
                  className="bop-input"
                  type="number"
                  min="0"
                  step="100"
                  value={formNuovo.ral}
                  onChange={fn('ral')}
                  placeholder="es. 30000"
                />
              </div>
            </div>
            <div className="bop-modal__footer">
              <button
                className="bop-btn bop-btn-secondary"
                onClick={() => setShowModalNuovo(false)}
                disabled={savingNuovo}
              >
                Annulla
              </button>
              <button
                className="bop-btn bop-btn-primary"
                onClick={handleSalvaNuovo}
                disabled={savingNuovo}
              >
                {savingNuovo ? 'Salvataggio…' : 'Salva'}
              </button>
            </div>
          </div>
        </div>
      )}

      <ApiInspector docs={API_DOCS} />
    </Layout>
  );
}
